import React from "react";
import ScheduleItem from "./ScheduleItem";
import ScheduleGraph from "./ScheduleGraph";
import { RECIPE_COLOURS } from "./DoserRecipePane";
import LoadingSpinner from "../loadingSpinner";
import { wsTrans } from "../../utils/utils";
import { contSaveSchedule, readJPro, readXBoards } from "../C18AiUtils";
import { cl, globs } from "../../../components/utils/utils";

const TEXT = "#2c3e50";
const TEXT_MUTED = "#8a9aac";
const BORDER = "#c5ced8";
const pageWrap = {
  fontFamily: "system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  padding: 20,
  width: "100%",
  boxSizing: "border-box",
};

const titleTyp = {
  fontSize: 22,
  fontWeight: 700,
  color: TEXT,
  margin: "0 0 12px 0",
};

const headerRow = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 10,
  padding: "0 0 8px 0",
  marginBottom: 6,
  borderBottom: `1px solid ${BORDER}`,
  fontSize: 12,
  fontWeight: 600,
  color: TEXT_MUTED,
};

const headId = { width: 28, flexShrink: 0, textAlign: "center" };
const headRecipe = { flex: "1 1 140px", minWidth: 140, maxWidth: 220 };
const headTime = { width: 130, flex: "0 0 auto" };
const headNotes = { flex: "1 1 160px", minWidth: 140, maxWidth: 280 };
const headAction = { width: 36, flexShrink: 0 };

const itemWrap = {
  marginBottom: 10,
};

const addRowBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  marginTop: 4,
  marginBottom: 14,
  padding: "8px 14px",
  borderRadius: 8,
  border: `1px dashed ${BORDER}`,
  background: "#fff",
  color: TEXT_MUTED,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const sortBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: 4,
  padding: 2,
  border: "none",
  background: "transparent",
  color: TEXT_MUTED,
  cursor: "pointer",
  verticalAlign: "middle",
};

function blankSlot() {
  return { recipeId: "none", dayMins: 0, durMins: 0, notes: "" };
}

function isActiveSlot(s) {
  return s && s.recipeId && s.recipeId !== "none";
}

function startOf(s) {
  const m = Math.round(Number(s && s.dayMins) || 0);
  return ((m % 1440) + 1440) % 1440;
}

/** simple is { recipeList: recipeId[], recipeSched: [{ start, recipeIdx, notes }] }. */
function scheduleFromSimple(simple) {
  const list = (simple && simple.recipeList) || [];
  return ((simple && simple.recipeSched) || []).map((s) => ({
    recipeId: list[s.recipeIdx] || "none",
    dayMins: Number(s.start) || 0,
    durMins: 0,
    notes: s.notes != null ? s.notes : "",
  }));
}

/** A recipe holds from its start until the next one. The span is written as
 *  durMins so the controller and the dashboard, which still read a length,
 *  follow the same rule. Two starts on one minute: the later row replaces
 *  the earlier, so the earlier length is zero. */
function withHeldDurations(schedule) {
  const active = [];
  (schedule || []).forEach((s, i) => {
    if (isActiveSlot(s)) active.push({ i, start: startOf(s) });
  });
  active.sort((a, b) => a.start - b.start || a.i - b.i);
  const durByIndex = {};
  for (let k = 0; k < active.length; k++) {
    const to = k + 1 < active.length ? active[k + 1].start : active[0].start + 1440;
    durByIndex[active[k].i] = to - active[k].start;
  }
  return (schedule || []).map((s, i) => ({
    ...s,
    durMins: durByIndex[i] != null ? durByIndex[i] : 0,
  }));
}

function SortIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7 10l5-5 5 5H7zm0 4l5 5 5-5H7z" />
    </svg>
  );
}

export default class DoserSchedule2 extends React.Component {
  constructor(props) {
    super(props);
//     cl(props);
    this.state = {
      loaded: false,
      recipes: [],
      schedule: [],
      jPlus: [],
      jPro: [],
      zInd: null,
      draftIndex: null,
      editSchedule: false,
    };
    this.subscribe_savePageEvent = globs.events.subscribe(
      "savePageEvent",
      this.savePage
    );
  }

  componentDidMount() {
    this.loadInfo();
  }

  componentDidUpdate(prev) {
    const simple = this.props.parms && this.props.parms.simple;
    const prevSimple = prev.parms && prev.parms.simple;
    if (!simple || simple === prevSimple || this.state.editSchedule || !this.state.loaded) return;
    const schedule = scheduleFromSimple(simple);
    this.adjustSchedule(schedule, this.state.jPlus);
    this.setState({ schedule });
  }

  componentWillUnmount() {
    if (this.subscribe_savePageEvent && this.subscribe_savePageEvent.remove) {
      this.subscribe_savePageEvent.remove();
    }
  }


  maxSlots = (jPlus) => {
    const used = (jPlus || []).filter((jp) => !jp.notUsed).length || 1;
    return 12 / used;
  };

  adjustSchedule = (schedule, jPlus) => {
    const schedCnt = this.maxSlots(jPlus);
    if (schedule.length < schedCnt) {
      for (let i = schedule.length; i < schedCnt; i++) {
        schedule.push(blankSlot());
      }
    } else {
      for (let i = schedule.length - 1; i >= schedCnt; i--) {
        delete schedule[i];
      }
      schedule.length = schedCnt;
    }
    for (let [i, s] of schedule.entries()) {
      if (!s) {
        schedule[i] = blankSlot();
      } else if (s.notes == null) {
        s.notes = "";
      }
      if (!s.dayMins) s.dayMins = 0;
    }
  };

  normalizeRecipes = (recipes) => {
    return (recipes || [])
      .filter((r) => r && r.recipeId && r.recipeId !== "none")
      .map((r, i) => ({
        ...r,
        color: r.color || RECIPE_COLOURS[i % RECIPE_COLOURS.length],
        notes: r.notes != null ? r.notes : "",
      }));
  };


  loadInfo = async () => {
    const pa = this.props.parms;
    const zone = globs.zonesInfo.info.find((z) => z.zoneId === pa.zone);
    const res = await wsTrans("usa", {
      cmd: "cRest",
      uri: "/s/pearlDoser",
      method: "retrieve",
      sessionId: globs.userData.session.sessionId,
      body: { zoneId: pa.zone },
    });
    const stateVals = {
      loaded: true,
//       mode:pa.mode,
      xBoards: readXBoards(zone.siteZoneIndex),
      zInd: zone.siteZoneIndex,
    };
    for (const val of res.data) {
      stateVals[val.type] = val.value;
    }
    const simple = (pa && pa.simple) || stateVals.simple;
    if (simple && Array.isArray(simple.recipeSched)) {
      stateVals.schedule = scheduleFromSimple(simple);
    }
    if (!stateVals.schedule) stateVals.schedule = [];
    this.adjustSchedule(stateVals.schedule, stateVals.jPlus);
    stateVals.jPro = readJPro(
      zone.siteZoneIndex,
      stateVals.xBoards,
      (stateVals.jPlus || []).length
    );
    stateVals.recipes = this.normalizeRecipes(stateVals.recipes);

    cl(stateVals)
    this.setState(stateVals);
  };

  markDirty = (enableSave = true) => {
    globs.events.publish("savePageEnable", !!enableSave);
    if (this.props.onChange) {
      this.props.onChange({ type: "change", value: this.savePage });
    }
    this.setState({ editSchedule: true });
  };

  /** Same payload as Programs. recipeList is the recipes used, in first-seen
   *  order. recipeSched is each start where the recipe changes. */
  saveRecipesAndSchedule = async(schedule) => {
//     cl(this.state)
    const { recipes } = this.state;
    const {onChange}=this.props
    const recipeLU = {};
    (recipes || []).forEach((r) => { recipeLU[r.recipeId] = r; });
    const rows = [];
    (schedule || []).forEach((s, i) => {
      if (isActiveSlot(s)) rows.push({ s, i, start: startOf(s), notes:s.notes });
    });
    rows.sort((a, b) => a.start - b.start || a.i - b.i);
    const uniq = [];
    rows.forEach((r) => {
      if (uniq.length && uniq[uniq.length - 1].start === r.start) uniq[uniq.length - 1] = r;
      else uniq.push(r);
    });
    let curRecipe;
    const recipeSched = [];
    const recipeList = [];
    const recipePos = {};
    uniq.forEach((r) => {
      const id = r.s.recipeId;
      if (id === curRecipe) return;
      if (recipePos[id] == null) {
        recipePos[id] = recipeList.length;
        recipeList.push(recipeLU[id]);
      }
      recipeSched.push({ start: r.start, recipeIdx: recipePos[id], notes:r.notes });
      curRecipe = id;
    });
//     cl(recipeList);
//     cl(recipeSched);
    let simple={recipeSched,recipeList:recipeList.map(r=>r.recipeId)}
    onChange({
      type:"update",
      value:{simple}
    })
//     cl(simple)
    await this.doSave("simple",simple)
    onChange({type:"update",simple})
    return { recipeList, recipeSched };
  };

  doSave = async (field, value) => {
    const pa = this.props.parms;
    cl(`save ${field}`)
    await wsTrans("usa", {
      cmd: "cRest",
      uri: "/s/pearlDoser",
      method: "update",
      sessionId: globs.userData.session.sessionId,
      body: {
        zoneId: pa.zone,
        type: field,
        value,
      },
    });
  };

  savePage = async () => {
    const st = this.state;
    let {onChange}=this.props
    globs.events.publish("savePageEnable", false);
    if (st.editSchedule) {
      const schedule = withHeldDurations(st.schedule);
      const { recipeList, recipeSched } = await this.saveRecipesAndSchedule(schedule);
      this.recipeList = recipeList;
      this.recipeSched = recipeSched;
      await this.doSave("schedule", schedule);
      await this.doSave("mode", "simple");
//       await contSaveSchedule(schedule, st.recipes, st.jPlus, st.jPro, st.zInd);
      this.setState({ schedule, editSchedule: false });
      if (onChange) {
        onChange({ type: "change", value: null });
        onChange({type:"update",value:{mode:"simple"},})
      }
    }
    globs.events.publish("saveOK", true);
  };

  getVisibleIndices = () => {
    const { schedule, draftIndex } = this.state;
    const indices = [];
    (schedule || []).forEach((s, i) => {
      if (isActiveSlot(s)) indices.push(i);
    });
    if (
      draftIndex != null &&
      schedule[draftIndex] &&
      !isActiveSlot(schedule[draftIndex]) &&
      !indices.includes(draftIndex)
    ) {
      indices.push(draftIndex);
    }
    return indices;
  };

  addRow = () => {
    const { schedule, jPlus, draftIndex } = this.state;
    if (draftIndex != null && schedule[draftIndex] && !isActiveSlot(schedule[draftIndex])) {
      return;
    }
    const max = this.maxSlots(jPlus);
    let next = schedule.slice(0);
    let idx = next.findIndex((s) => !isActiveSlot(s));
    if (idx < 0) {
      if (next.length >= max) return;
      next.push(blankSlot());
      idx = next.length - 1;
    } else {
      next[idx] = blankSlot();
    }
    this.setState({ schedule: next, draftIndex: idx });
    this.markDirty();
  };

  sortByTime = () => {
    const { schedule, draftIndex } = this.state;
    const active = [];
    const inactive = [];
    let draftRow = null;
    schedule.forEach((s, i) => {
      if (draftIndex === i && !isActiveSlot(s)) {
        draftRow = s;
      } else if (isActiveSlot(s)) {
        active.push(s);
      } else {
        inactive.push(s);
      }
    });
    active.sort(
      (a, b) => (Number(a.dayMins) || 0) - (Number(b.dayMins) || 0)
    );
    const next = [...active, ...inactive];
    if (draftRow) next.push(draftRow);
    while (next.length < schedule.length) next.push(blankSlot());
    if (next.length > schedule.length) next.length = schedule.length;

    const newDraft =
      draftIndex != null && draftRow
        ? next.findIndex((s) => s === draftRow)
        : null;

    this.setState({
      schedule: next,
      draftIndex: newDraft >= 0 ? newDraft : null,
    });
    this.markDirty();
  };

  onItemChange = (values) => {
    const idx = values.index - 1;
    const schedule = this.state.schedule.slice(0);
    if (!schedule[idx]) return;

    switch (values.type) {
      case "time":
        schedule[idx] = { ...schedule[idx], dayMins: values.value };
        break;
      case "recipe":
        schedule[idx] = { ...schedule[idx], recipeId: values.value };
        break;
      case "notes":
        schedule[idx] = { ...schedule[idx], notes: values.value };
        break;
      case "delete":
        schedule[idx] = blankSlot();
        this.setState({
          schedule,
          draftIndex:
            this.state.draftIndex === idx ? null : this.state.draftIndex,
        });
        this.markDirty();
        return;
      default:
        break;
    }

    const draftIndex =
      this.state.draftIndex === idx && isActiveSlot(schedule[idx])
        ? null
        : this.state.draftIndex;

    this.setState({ schedule, draftIndex });
    this.markDirty();
  };

  renderHeader() {
    return (
      <div style={headerRow}>
        <div style={headId}>ID</div>
        <div style={headRecipe}>Recipe</div>
        <div style={headTime}>
          Available from
          <button
            type="button"
            style={sortBtn}
            title="Sort"
            aria-label="Sort by available from"
            onClick={this.sortByTime}
          >
            <SortIcon />
          </button>
        </div>
        <div style={headNotes}>Notes</div>
        <div style={headAction} />
      </div>
    );
  }


  renderSimple() {
    const { recipes, schedule } = this.state;
    const visible = this.getVisibleIndices();

    return (
      <>
        {this.renderHeader()}

        {visible.map((i) => {
          const row = schedule[i];
          const recipe = (recipes || []).find((r) => r.recipeId === row.recipeId);
          return (
            <div key={i} style={itemWrap}>
              <ScheduleItem
                idx={i + 1}
                recipeId={row.recipeId}
                recipes={recipes}
                color={recipe ? recipe.color : null}
                dayMins={row.dayMins}
                notes={row.notes}
                err={row.err}
                onChange={this.onItemChange}
              />
            </div>
          );
        })}

        <button type="button" style={addRowBtn} onClick={this.addRow}>
          + Add row
        </button>

        <ScheduleGraph schedule={schedule} recipes={recipes} />
      </>
    );
  }

  enableSimple=()=>{
    let {onChange}=this.props
//     cl("enableSimple")
//     onChange({
//       type:"update",
//       value:{mode:"simple"},
//     })
    globs.events.publish("savePageEnable", true);
    onChange({ type: "change", value: this.savePage });
    this.setState({ mode:"simple",editSchedule: true });
  }

  render() {
    const { loaded, mode } = this.state;
    const {onChange} = this.props
//     const {mode}=this.props.parms
//     cl(mode)
    if (!loaded) {
      return <LoadingSpinner message="loading schedule..." />;
    }

    return (
      <div style={{position:"relative"}}>
        <div id="/doserSchedule_simple" style={pageWrap}>
          <h2 style={titleTyp}>Nutrient Availability Schedule&nbsp;
            <span
              style={{color:"#4080C0",cursor:"pointer"}}
              onClick={e=>{onChange({type:"click",value:"info",
                page:"schedule", src:"simple"})}}
            >Info</span>&nbsp;
            {mode!="simple"&&
              <>
                <button type="button" className="filled"
                  style={{position:"relative",zIndex:5}}
                  onClick={this.enableSimple}
                  >Enable</button>
                <div style={{position:"absolute",top:0,width:"100%",
                  height:"100%",background:"black",opacity:0.1}}/>
              </>
            }
          </h2>
          {this.renderSimple()}
        </div>
      </div>
    );
  }
}
